<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="loading" />
    <div
      v-else-if="program"
      class="flex flex-1 flex-col"
    >
      <div class="bg-white shadow-md z-10">
        <ProgramHeader :program="program" class="p-3" />
        <nav
          v-if="!!program"
          class="flex border-collapse space-x-2 overflow-x-auto"
          aria-label="Tabs"
        >
          <router-link
            :to="{ name: 'program.detail.content' }"
            active-class="nav-item-active"
            class="nav-item"
          >
            Programme
          </router-link>
          <router-link
            :to="{ name: 'program.detail.consumers'}"
            active-class="nav-item-active"
            class="nav-item"
          >
            {{ program.programmable_type === 'loyalty-card' ? 'Cartes clients' : 'Patients concernés' }}
          </router-link>
          <!-- A loyalty card is settled against its rules, not its resources:
               this is where a dispute with a pharmacist gets answered. -->
          <router-link
            v-if="program.programmable_type === 'loyalty-card'"
            :to="{name: 'program.loyalty-card.rules', params: {loyaltyCardId: program.programmable_id}}"
            active-class="nav-item-active"
            class="nav-item"
          >
            Règles de fonctionnement
          </router-link>
          <router-link
            v-else
            :to="{ name: 'program.detail.attachments'}"
            active-class="nav-item-active"
            class="nav-item"
          >
            Informations et ressources
          </router-link>
          <router-link
            v-if="program.programmable_type === 'support'"
            :to="{ name: 'program.detail.pathway' }"
            active-class="nav-item-active"
            class="nav-item"
          >
            Parcours de soin
          </router-link>
          <router-link
            v-if="program.programmable_type === 'support' && program.allow_custom_product_recommendations"
            :to="{ name: 'program.detail.product-recommendations', params: {supportProgramId: program.programmable_id} }"
            active-class="nav-item-active"
            class="nav-item"
          >
            Produits conseils
          </router-link>
          <!-- A laboratory card only follows the laboratory's rules: a pharmacy
               cannot add animations to it. -->
          <router-link
            v-if="program.programmable_type === 'loyalty-card' && !isLaboratoryProgram(program)"
            :to="{ name: 'program.detail.animations', query: {loyaltyCardId: program.programmable_id} }"
            active-class="nav-item-active"
            class="nav-item"
          >
            Animations
          </router-link>
          <router-link
            v-if="program.programmable_type === 'loyalty-card'"
            :to="{name: 'program.loyalty-card.products', params: {loyaltyCardId: program.programmable_id}}"
            active-class="nav-item-active"
            class="nav-item"
          >
            Produits
          </router-link>
          <router-link
            v-if="program.programmable_type === 'feature'"
            :to="{name: 'program.detail.customize', params: {medicineCabinetId: program.programmable_id}}"
            active-class="nav-item-active"
            class="nav-item"
          >
            Personnalisation
          </router-link>
          <router-link
            v-if="program.notification_sequences_count"
            :to="{name: 'program.detail.notification-sequences', params: {programId: program.id}}"
            active-class="nav-item-active"
            class="nav-item flex gap-1"
          >
            <span>Suivi automatique</span>
            <NewFeatureTag />
          </router-link>
        </nav>
      </div>
      <router-view :key="$route.path" :program="program" />
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
import {ref, watch} from "vue";
import {useRoute} from "vue-router";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import ProgramHeader from "@/program/components/program-header/ProgramHeader.vue";
import {programApi} from "@/container";
import {Program} from "@/program/interfaces/Program";
import {isLaboratoryProgram} from "@/program/services/ProgramService";
import NewFeatureTag from "@/core/components/tags/NewFeatureTag.vue";

const route = useRoute();

const program = ref<Program>();
const loading = ref(true);

const read = async (id: number) => {
    loading.value = true;
    try {
        program.value = await programApi().read(id);
    } finally {
        loading.value = false;
    }
};

watch(() => route.params.programId, async (programId) => {
    if (programId) {
        await read(Number(programId));
    }
}, {immediate: true});
</script>

<style scoped>
.nav-item {
    @apply border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-3 border-b-2 font-medium
}

.nav-item-active {
    @apply border-primary text-primary
}
</style>
